/**
 * Barcode Utilities
 * 
 * Helper functions for parsing and validating barcodes
 */

/**
 * Barcode types
 */
export const BarcodeType = {
  EAN13: 'ean13',
  EAN8: 'ean8',
  CODE128: 'code128',
  CODE39: 'code39',
  QRCODE: 'qrcode',
  UNKNOWN: 'unknown',
};

/**
 * Detect barcode type from scanned value
 */
export function detectBarcodeType(value) {
  if (!value || typeof value !== 'string') {
    return BarcodeType.UNKNOWN;
  }
  
  // Clean the value
  const cleaned = value.trim();
  
  // EAN-13: 13 digits
  if (/^\d{13}$/.test(cleaned)) {
    return BarcodeType.EAN13;
  }
  
  // EAN-8: 8 digits
  if (/^\d{8}$/.test(cleaned)) {
    return BarcodeType.EAN8;
  }
  
  // Code 128: Alphanumeric, variable length
  if (/^[A-Za-z0-9\-\.\/\s]+$/.test(cleaned) && cleaned.length > 0) {
    // If it's all numbers but not EAN, it might be Code 128
    if (/^\d+$/.test(cleaned) && cleaned.length !== 8 && cleaned.length !== 13) {
      return BarcodeType.CODE128;
    }
    // If it has letters, likely Code 128
    if (/[A-Za-z]/.test(cleaned)) {
      return BarcodeType.CODE128;
    }
  }
  
  // Code 39: Uppercase letters, digits, and special chars
  if (/^[A-Z0-9\-\.\s\$\/\+\%]+$/.test(cleaned.toUpperCase())) {
    return BarcodeType.CODE39;
  }
  
  // QR Code: Can contain any data
  // If it's a URL or contains special patterns
  if (cleaned.includes('http') || cleaned.includes('://')) {
    return BarcodeType.QRCODE;
  }
  
  return BarcodeType.UNKNOWN;
}

/**
 * Validate EAN-13 checksum
 */
export function validateEAN13(code) {
  if (!/^\d{13}$/.test(code)) {
    return false;
  }
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(code[i], 10);
    sum += i % 2 === 0 ? digit : digit * 3;
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(code[12], 10);
}

/**
 * Validate EAN-8 checksum
 */
export function validateEAN8(code) {
  if (!/^\d{8}$/.test(code)) {
    return false;
  }
  
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    const digit = parseInt(code[i], 10);
    sum += i % 2 === 0 ? digit * 3 : digit;
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(code[7], 10);
}

/**
 * Validate barcode based on type
 */
export function validateBarcode(code, type = null) {
  if (!code) return false;
  
  const detectedType = type || detectBarcodeType(code);
  
  switch (detectedType) {
    case BarcodeType.EAN13:
      return validateEAN13(code);
    case BarcodeType.EAN8:
      return validateEAN8(code);
    case BarcodeType.CODE128:
    case BarcodeType.CODE39:
    case BarcodeType.QRCODE:
      return code.length > 0;
    default:
      return true;
  }
}

/**
 * Parse barcode data (for QR codes that might contain structured data)
 */
export function parseBarcodeData(code) {
  if (!code) return { raw: code };
  
  // Try to parse as JSON
  try {
    const parsed = JSON.parse(code);
    return { ...parsed, raw: code };
  } catch {
    // Not JSON, continue
  }
  
  // Try to parse as URL params
  if (code.includes('?') || code.includes('&')) {
    try {
      const url = new URL(code, 'http://dummy');
      const params = Object.fromEntries(url.searchParams);
      if (Object.keys(params).length > 0) {
        return { ...params, raw: code, url: code };
      }
    } catch {
      // Not a valid URL
    }
  }
  
  // Return raw value
  return { raw: code };
}

/**
 * Format barcode for display
 */
export function formatBarcode(code, type = null) {
  if (!code) return '';
  
  const detectedType = type || detectBarcodeType(code);
  
  switch (detectedType) {
    case BarcodeType.EAN13:
      // Format as X-XXXXXX-XXXXX-X
      return `${code.slice(0, 1)}-${code.slice(1, 7)}-${code.slice(7, 12)}-${code.slice(12)}`;
    case BarcodeType.EAN8:
      // Format as XXXX-XXXX
      return `${code.slice(0, 4)}-${code.slice(4)}`;
    default:
      return code;
  }
}

/**
 * Generate EAN-13 check digit
 */
export function generateEAN13CheckDigit(code12) {
  if (!/^\d{12}$/.test(code12)) {
    throw new Error('Code must be exactly 12 digits');
  }
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(code12[i], 10);
    sum += i % 2 === 0 ? digit : digit * 3;
  }
  
  return ((10 - (sum % 10)) % 10).toString();
}

/**
 * Detect if input is from a barcode scanner
 * Scanners typically input data very quickly
 */
export function createScannerDetector(options = {}) {
  const {
    maxInputTime = 50, // Max ms between keystrokes for scanner
    minLength = 3,     // Minimum barcode length
    onScan = () => {},
    onInput = () => {},
  } = options;
  
  let buffer = '';
  let lastKeyTime = 0;
  
  return {
    handleKeyPress: (event) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;
      
      // If too much time has passed, reset buffer
      if (timeDiff > maxInputTime && buffer.length > 0) {
        // This was manual typing
        onInput(buffer);
        buffer = '';
      }
      
      lastKeyTime = currentTime;
      
      // Handle Enter key (end of scan)
      if (event.key === 'Enter') {
        if (buffer.length >= minLength) {
          // This looks like a scanner input
          event.preventDefault();
          onScan(buffer);
        } else if (buffer.length > 0) {
          onInput(buffer);
        }
        buffer = '';
        return;
      }
      
      // Add printable characters to buffer
      if (event.key.length === 1) {
        buffer += event.key;
      }
    },
    
    reset: () => {
      buffer = '';
      lastKeyTime = 0;
    },
    
    getBuffer: () => buffer,
  };
}

export default {
  BarcodeType,
  detectBarcodeType,
  validateEAN13,
  validateEAN8,
  validateBarcode,
  parseBarcodeData,
  formatBarcode,
  generateEAN13CheckDigit,
  createScannerDetector,
};
